/**
 * Pure helpers for the builder's free-form ("draw anywhere") layout — kept out
 * of the components so the maths can be unit-tested without a browser.
 *
 * A container whose `layout` prop is 'free' places its children by left/top
 * (percent of the container) instead of stacking them. Every position and size
 * is stored as a PERCENTAGE of the container, so a design scales with the page
 * instead of breaking at another screen width; and on smaller screens the page
 * re-flows it by itself (see collectAutoStackRules in Portal/landingPageStyles.js),
 * so nothing has to be set per device.
 */
import { findNode, findParentContainerId, ROOT_ID } from './treeHelpers';

export const SNAP_THRESHOLD = 1.5; // percent of the container
export const ROTATE_SNAP_STEP = 15; // degrees
export const ROTATE_SNAP_TOLERANCE = 4;

// Free placement comes in two flavours: 'free' re-flows itself on smaller screens
// (see buildResponsiveCss); 'free-manual' leaves every device to the author.
export function isFreeContainer(node) {
    return node?.props?.layout === 'free' || node?.props?.layout === 'free-manual';
}

/**
 * The style a component gets when it is dropped/added into a free container:
 * floating (each new one nudged down/right so they don't sit exactly on top of
 * each other). Only the base is set — on smaller screens the container stacks
 * its floating children by itself, so no per-device style is needed.
 */
export function floatingStyleFor(siblingCount = 0) {
    const step = Math.min(siblingCount, 8) * 4;
    return {
        desktop: { position: 'absolute', left: `${8 + step}%`, top: `${8 + step}%`, width: '30%' },
    };
}

/**
 * After a component was inserted (click-add or drag from the palette), give it
 * floating placement if — and only if — its parent is a free container.
 */
export function withFreePlacement(nodes, nodeId) {
    const parentId = findParentContainerId(nodes, nodeId);
    if (!parentId || parentId === ROOT_ID) return nodes;
    const parent = findNode(nodes, parentId);
    if (!isFreeContainer(parent)) return nodes;
    const style = floatingStyleFor((parent.children || []).length - 1);
    const apply = (list) => list.map((n) => {
        if (n.id === nodeId) return { ...n, style: { ...style, ...n.style } };
        return n.children ? { ...n, children: apply(n.children) } : n;
    });
    return apply(nodes);
}

/**
 * Snaps a dragged box to the container's edges and centre lines.
 * left/top/width/height are percentages of the container.
 * Returns the snapped position plus which guides fired (for drawing them).
 */
export function snapPosition({ left, top, width, height }, threshold = SNAP_THRESHOLD) {
    const xTargets = [
        { value: 0, guide: 'start' },
        { value: (100 - width) / 2, guide: 'center' },
        { value: 100 - width, guide: 'end' },
    ];
    const yTargets = [
        { value: 0, guide: 'start' },
        { value: (100 - height) / 2, guide: 'center' },
        { value: 100 - height, guide: 'end' },
    ];
    const pick = (current, targets) => {
        let best = null;
        for (const t of targets) {
            const distance = Math.abs(current - t.value);
            if (distance <= threshold && (!best || distance < best.distance)) best = { ...t, distance };
        }
        return best;
    };
    const x = pick(left, xTargets);
    const y = pick(top, yTargets);
    return {
        left: x ? x.value : left,
        top: y ? y.value : top,
        guideX: x ? x.guide : null,
        guideY: y ? y.guide : null,
    };
}

/** Position that aligns a box of the given size to a side/centre of its container. */
export function alignPosition(how, { width, height }) {
    switch (how) {
        case 'left': return { left: 0 };
        case 'center': return { left: (100 - width) / 2 };
        case 'right': return { left: 100 - width };
        case 'top': return { top: 0 };
        case 'middle': return { top: (100 - height) / 2 };
        case 'bottom': return { top: 100 - height };
        default: return {};
    }
}

/** Arrow-key nudge: 1% per press, 5% with Shift. Returns null for a non-arrow key. */
export function nudgeDelta(key, shift) {
    const step = shift ? 5 : 1;
    switch (key) {
        case 'ArrowLeft': return { dx: -step, dy: 0 };
        case 'ArrowRight': return { dx: step, dy: 0 };
        case 'ArrowUp': return { dx: 0, dy: -step };
        case 'ArrowDown': return { dx: 0, dy: step };
        default: return null;
    }
}

export const clampPercent = (n, max = 100) => Math.min(max, Math.max(0, n));

/**
 * Angle (degrees, 0 = pointing straight up, clockwise) of the mouse around a
 * centre point — what the rotate handle sets. Snaps to every 15° when close.
 */
export function angleFromPoint(cx, cy, x, y, { snap = true } = {}) {
    let deg = (Math.atan2(y - cy, x - cx) * 180) / Math.PI + 90;
    deg = ((Math.round(deg) % 360) + 360) % 360;
    if (snap) {
        const nearest = Math.round(deg / ROTATE_SNAP_STEP) * ROTATE_SNAP_STEP;
        if (Math.abs(deg - nearest) <= ROTATE_SNAP_TOLERANCE) deg = nearest % 360;
    }
    return deg;
}

/** The transform value for a rotation, or '' (removes it) at 0°. */
export function rotationValue(deg) {
    return deg % 360 === 0 ? '' : `rotate(${deg}deg)`;
}

/** Reads the angle back out of a stored `transform: rotate(Ndeg)` value. */
export function parseRotation(transform) {
    const match = /rotate\((-?\d+(?:\.\d+)?)deg\)/.exec(transform || '');
    return match ? Number(match[1]) : 0;
}
